import {
  Autocomplete,
  Button,
  CircularProgress,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Box } from '@mui/system';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import { usePageView } from 'src/hooks/use-page-view';
import { GET_PATIENT_RECORD } from 'src/redux/actions/patientAction';
import {
  addTaskToTherapist,
  getAllTaskTypeAction,
  getTaskAvailabilityAction,
  resetAddNewTaskTherapist,
  resetTaskAvailabilityAction,
} from 'src/redux/actions/taskTypeAction';
import * as Yup from 'yup';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { appointmentBookFlag } from 'src/config';
import { schedulingTestId } from '../test-ids';

type Props = {
  handleCloseAddTaskForm: any;
  currentTab: string;
  date: string;
  selectedTaskType?: string | string[];
  source?: any;
  patient?: any;
};

const AddTaskForm = (props: Props) => {
  const [searchName, setSearchName] = useState('');
  const [intervalTime, setIntervalTime] = useState<ReturnType<typeof setInterval> | null>(null);
  const [searchDefaultValue] = useState('');
  const [error, setError] = useState('');
  const [patientSelectionMode, setPatientSelectionMode] = useState<'facility' | 'manual'>(
    'facility'
  );
  const [manualInputValue, setManualInputValue] = useState<string>('');
  usePageView();
  const dispatch = useDispatch();
  const { handleCloseAddTaskForm, currentTab, source, patient } = props;

  // THIN-8417: true when the patient has no certification in this tab's discipline on the task
  // date. Prefer activeCertDisciplines (computed for taskDate); fall back to the discipline-less
  // hasActiveCertForDiscipline for a patient object that did not come from the list call.
  const lacksCertForTab = (p: any) =>
    Array.isArray(p?.activeCertDisciplines)
      ? !p.activeCertDisciplines.includes(currentTab)
      : p?.hasActiveCertForDiscipline === false;
  const [pageNo, setPageNo] = useState(0);
  const getAllPatients = useSelector((state: any) => state.getPatientReducer?.response);
  const gettingPatientList = useSelector((state: any) => state.getPatientReducer);
  const [providerId] = useCurrentUser(['providerId']);
  const taskType = useSelector((state: any) => state.getAllTherapistTaskTypeReducer?.data);
  const isTaskAdded = useSelector((state: any) => state.AddTaskToTherapistReducer);
  const editabilityByDate = useSelector(
    (state: any) =>
      state.getTherapyPlanningDataReducer_2_0?.therapyPlaningFormattedData?.editabilityByDate
  );
  const editabilityForThisDate = editabilityByDate?.[props.date];
  const isConcurrentAllowedForThisDate = editabilityForThisDate?.isConcurrentAllowed ?? true;
  const isGroupAllowedForThisDate = editabilityForThisDate?.isGroupAllowed ?? true;
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');
  const taskAvailabilityResponse = useSelector(
    (state: any) => state.taskAvailabilityReducer?.response
  );
  const scheduledTaskTypes: string[] = taskAvailabilityResponse?.scheduledTaskTypes || [];

  // Default tasksType based on what's already scheduled for the patient.
  // - If anything is already scheduled → default to THERAPY
  // - Otherwise (or once it empties out again) → default to EVALUATION
  // We track whether the current value was set by this auto-default so the
  // field follows scheduledTaskTypes changes, but a user's explicit pick
  // (anything that isn't currently scheduled) stays put.
  const autoDefaultedTasksTypeRef = React.useRef(false);
  useEffect(() => {
    const normalize = (v: string) => (v ?? '').toString().replace(/[\s_]/g, '').toUpperCase();
    const normalizedScheduled = (scheduledTaskTypes ?? []).map(normalize);
    const evalScheduled = normalizedScheduled.includes('EVALUATION');
    const therapyScheduled = normalizedScheduled.includes('THERAPY');

    let desiredDefault: string;
    if (patientSelectionMode === 'manual') {
      // Manual mode only exposes EVALUATION + THERAPY.
      // - both scheduled → no preselection (both disabled)
      // - only one scheduled → pick the other
      // - none scheduled → EVALUATION
      desiredDefault =
        evalScheduled && therapyScheduled
          ? ''
          : evalScheduled
          ? 'THERAPY'
          : therapyScheduled
          ? 'EVALUATION'
          : 'EVALUATION';
    } else {
      // Facility mode: keep existing behavior — pick THERAPY when anything is
      // already scheduled, otherwise EVALUATION. If THERAPY itself is the
      // scheduled type, fall back to EVALUATION so we don't preselect a
      // disabled option.
      desiredDefault =
        normalizedScheduled.length > 0
          ? therapyScheduled
            ? 'EVALUATION'
            : 'THERAPY'
          : 'EVALUATION';
    }

    const currentTasksType = formik?.values?.tasksType;
    const currentIsScheduled =
      currentTasksType && normalizedScheduled.includes(normalize(currentTasksType));
    const shouldOverride =
      !currentTasksType || currentIsScheduled || autoDefaultedTasksTypeRef.current;
    if (shouldOverride && currentTasksType !== desiredDefault) {
      formik.setFieldValue('tasksType', desiredDefault);
      autoDefaultedTasksTypeRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskAvailabilityResponse, patientSelectionMode]);

  // Dynamic validation schema based on patient selection mode
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      patientId:
        patientSelectionMode === 'manual' || source === 'ALERT_TASK'
          ? Yup.string()
          : Yup.string().required('Please enter valid Patient Name'),
      tasksType: Yup.string().required('Please enter valid Task'),
      numberOfMinutes: Yup.number()
        .required('Please enter valid Number of Minutes')
        .positive('Please enter valid Number of Minutes'),
      // description: Yup.string().required('Please enter valid Description'),
      discipline: Yup.mixed().required(),
      localDate: Yup.mixed(),
      taskNote:
        patientSelectionMode === 'manual'
          ? Yup.string().required('Note for Therapist is required')
          : Yup.mixed(),
    });
  }, [patientSelectionMode, source]);

  const getInitialValues = () => ({
    localDate: '',
    patientId: source === 'ALERT_TASK' ? patient?.uuid ?? patient?.patientUuid ?? '' : '',
    tasksType: '',
    numberOfMinutes: '',
    // description: '',
    discipline: '',
    taskNote: '',
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    validationSchema: validationSchema,
    validateOnMount: false,
    onSubmit: async (values): Promise<void> => {
      const { taskNote, patientId, ...restValues } = values;

      // Prepare payload based on patient selection mode
      const payloadData: any = { ...restValues, taskNote: taskNote ? taskNote : null };

      // If source is ALERT_TASK, use patient?.uuid as patientId — but only
      // while the user has stayed on the facility selection. If they switched
      // to "Enter a Future Admission manually", honor that flow instead so
      // the task is created as a future admission for the typed name.
      if (source === 'ALERT_TASK' && patientSelectionMode !== 'manual') {
        payloadData.patientId = patient?.patientUuid || patient?.uuid;
        payloadData.isFutureAdmission = false;
      } else if (patientSelectionMode === 'manual') {
        // In manual mode:
        // - If patientId is a UUID (user selected from list), send patientId with uuid and isFutureAdmission as true
        // - If patientId is empty or not a UUID (user typed something not in list), use futureAdmissionName
        const selectedPatient = patientId
          ? allPatients.find((p: any) => p.uuid === patientId)
          : null;

        if (selectedPatient && patientId) {
          // Patient selected from dropdown (has UUID)
          payloadData.patientId = patientId;
          payloadData.isFutureAdmission = true;
        } else {
          // User typed a value not in the list
          payloadData.futureAdmissionName = manualInputValue || '';
          payloadData.isFutureAdmission = true;
        }
      } else {
        payloadData.patientId = patientId;
        payloadData.isFutureAdmission = false;
      }

      try {
        dispatch(
          addTaskToTherapist({
            providerId,
            data: payloadData,
            isV2: true,
            type: props?.selectedTaskType,
          })
        );
      } catch (err) {
        console.error(err);
      }
    },
  });

  // Update validation schema when patient selection mode changes
  const didMountModeEffectRef = React.useRef(false);
  useEffect(() => {
    // On initial mount with ALERT_TASK source, don't wipe the seeded
    // patientId — that would erase the alert's patient before the user
    // sees the form.
    const isFirstRun = !didMountModeEffectRef.current;
    didMountModeEffectRef.current = true;
    if (isFirstRun && source === 'ALERT_TASK') {
      return;
    }

    // Clear any pending intervals when mode changes
    if (intervalTime) {
      clearInterval(intervalTime);
      setIntervalTime(null);
    }

    formik.setFieldTouched('patientId', false);
    formik.setFieldTouched('taskNote', false);
    if (patientSelectionMode === 'manual') {
      formik.setFieldValue('patientId', '');
      setManualInputValue('');
      setSearchName('');
      setSelectedPatientData(null);
      // Default task type honors scheduledTaskTypes:
      // - both EVALUATION + THERAPY scheduled → no preselection (both disabled)
      // - only one scheduled → pick the other
      // - none scheduled → EVALUATION
      const normalize = (v: string) => (v ?? '').toString().replace(/[\s_]/g, '').toUpperCase();
      const normalizedScheduled = (scheduledTaskTypes ?? []).map(normalize);
      const evalScheduled = normalizedScheduled.includes('EVALUATION');
      const therapyScheduled = normalizedScheduled.includes('THERAPY');
      const manualDefault =
        evalScheduled && therapyScheduled ? '' : evalScheduled ? 'THERAPY' : 'EVALUATION';
      formik.setFieldValue('tasksType', manualDefault);
    } else {
      // Reset taskNote requirement when switching back to facility mode
      formik.setFieldTouched('taskNote', false);
      setManualInputValue('');
      setSelectedPatientData(null);
      // Clear selected patient and reset search to trigger API call with includePreadmit: false
      formik.setFieldValue('patientId', '');
      setSearchName('');
      setPageNo(0);
      setAllPatients([]);
    }
    // Revalidate form when mode changes
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientSelectionMode]);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     dispatch(getAllPatientAction());
  //   }, 600);

  //   return () => {
  //     clearTimeout(timer);
  //   };
  // }, [searchName]);

  useEffect(() => {
    dispatch(
      GET_PATIENT_RECORD({
        data: {
          searchString: searchName,
          includePreadmit: patientSelectionMode === 'manual',
        },
        params: {
          active: true,
          page: pageNo,
          pageSize: 20,
          // THIN-8417: no activeDisciplines filter. It keeps only patients with a cert covering
          // today, hiding pending evals, recert gaps and pre-admits. Therapy is disabled per
          // patient from activeCertDisciplines, which the backend computes for taskDate.
          taskDate: props.date,
        },
      })
    );
  }, [pageNo, searchName, patientSelectionMode, props.date, currentTab, dispatch]);

  useEffect(() => {
    dispatch(
      getAllTaskTypeAction({
        providerId,
        active: false,
      })
    );
    formik.setFieldValue('localDate', props.date);
  }, []);

  useEffect(() => {
    if (taskType?.length > 0) {
      formik.setFieldValue('tasksType', 'EVALUATION');
    }
    formik.setFieldValue('discipline', currentTab);
  }, [taskType, currentTab]);

  useEffect(() => {
    if (!isTaskAdded.isTaskAdded && isTaskAdded?.response === null) return;

    if (!isTaskAdded.isGetAvailability && isTaskAdded?.response !== null) {
      setError(isTaskAdded?.response?.message);
      dispatch(resetAddNewTaskTherapist());
      return;
    }
  }, [isTaskAdded, dispatch]);

  const [allPatients, setAllPatients] = useState<any>([]);
  const [selectedPatientData, setSelectedPatientData] = useState<any>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!Array.isArray(getAllPatients?.content)) return;
    if (pageNo === 0) {
      setAllPatients(getAllPatients?.content);
    } else {
      setAllPatients((prev: any) => [...prev, ...getAllPatients.content]);
    }
  }, [getAllPatients]);

  // Compute options for manual mode to include selected patient (without footer button)
  const manualModeOptions = useMemo(() => {
    if (patientSelectionMode !== 'manual') return allPatients;

    let options = [...allPatients];

    // Add selected patient if not in list
    if (selectedPatientData) {
      const isInList = allPatients.some((p: any) => p.uuid === selectedPatientData.uuid);
      if (!isInList) {
        options = [selectedPatientData, ...options];
      }
    }

    // Don't add "Add new admission" option here - it will be rendered as footer

    return options;
  }, [allPatients, selectedPatientData, patientSelectionMode]);

  // Get the current value for the Autocomplete based on selectedPatientData or formik.values.patientId
  const getCurrentValue = useMemo(() => {
    if (patientSelectionMode !== 'manual') return null;
    if (selectedPatientData) {
      return selectedPatientData;
    }
    if (formik.values.patientId) {
      const found = allPatients.find((p: any) => p.uuid === formik.values.patientId);
      return found || null;
    }
    return null;
  }, [patientSelectionMode, selectedPatientData, formik.values.patientId, allPatients]);

  const handleScrollCapture = (event: any) => {
    const scrollThreshold = 1;
    if (
      Math.abs(
        (event.target as any).scrollHeight -
          (event.target as any).scrollTop -
          (event.target as any).clientHeight
      ) <= scrollThreshold
    ) {
      // if (!getAllPayerPlan?.response?.data?.last) {
      setPageNo((prev) => prev + 1);
      // }
    }
  };

  // Custom ListboxComponent for manual mode (footer is handled by CustomPaper)
  const CustomListbox = React.forwardRef<HTMLUListElement, any>((props, ref) => {
    const { children, ...other } = props;

    return (
      <ul
        ref={ref}
        {...other}
        style={{
          ...other.style,
          maxHeight: '200px',
          margin: 0,
        }}
      >
        {children}
      </ul>
    );
  });
  CustomListbox.displayName = 'CustomListbox';

  // Custom Paper component that always shows footer button
  const CustomPaper = React.forwardRef<HTMLDivElement, any>((props, ref) => {
    const { children, ...other } = props;
    return (
      <Paper
        ref={ref}
        {...other}
        sx={{
          ...other.sx,
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {children}
        {/* Footer button - positioned right after the listbox content */}
        <Box
          sx={{
            borderTop: '1px solid #E5E7EB',
            padding: '8px 16px',
            backgroundColor: '#FFFFFF',
            position: 'relative',
            zIndex: 10,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setAutocompleteOpen(false);
            setTimeout(() => {
              const inputElement = document.querySelector(
                '#combo-box-demo-manual input[type="text"]'
              ) as HTMLInputElement;
              if (inputElement) {
                inputElement.focus();
              }
            }, 100);
          }}
        >
          <Button
            startIcon={<Add sx={{ color: '#6366F1' }} />}
            sx={{
              color: '#6366F1',
              textTransform: 'none',
              fontWeight: 400,
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            Add new admission
          </Button>
        </Box>
      </Paper>
    );
  });
  CustomPaper.displayName = 'CustomPaper';

  const selectedFacilityPatient = useMemo(() => {
    if (!formik.values.patientId) return null;
    return allPatients.find((p: any) => p.uuid === formik.values.patientId) || null;
  }, [allPatients, formik.values.patientId]);

  const effectivePatient = useMemo(() => {
    if (source === 'ALERT_TASK') {
      // If the user picked a different patient from the dropdown after the
      // alert opened the form, follow that selection instead of the original
      // alert payload — otherwise the form stays bound to the alert patient
      // (isOutPatient / openEocId / etc.) and the tasksType dropdown won't
      // refresh.
      const alertUuid = patient?.uuid ?? patient?.patientUuid;
      const currentId = formik?.values?.patientId;
      if (currentId && currentId !== alertUuid) {
        return (
          allPatients.find((p: any) => p?.uuid === currentId) ??
          selectedFacilityPatient ??
          selectedPatientData ??
          null
        );
      }
      // Default ALERT_TASK path: enrich the alert payload with the matching
      // patient-list record so rich fields drive isTaskTypeDisabled the same
      // way they do in facility mode.
      const enriched = alertUuid ? allPatients.find((p: any) => p?.uuid === alertUuid) : null;
      return enriched ? { ...patient, ...enriched } : patient;
    }
    if (patientSelectionMode === 'manual') return selectedPatientData;
    return selectedFacilityPatient;
  }, [
    source,
    patient,
    patientSelectionMode,
    selectedPatientData,
    selectedFacilityPatient,
    allPatients,
    formik?.values?.patientId,
  ]);

  // Fetch task-availability for the selected patient/date/discipline. The response
  // gives us scheduledTaskTypes — the task types already scheduled for this patient on
  // this day, which we disable in the dropdown below.
  useEffect(() => {
    const patientId =
      effectivePatient?.uuid || effectivePatient?.patientUuid || formik.values.patientId;
    if (!providerId || !patientId || !props.date || !currentTab) {
      dispatch(resetTaskAvailabilityAction());
      return;
    }
    dispatch(
      getTaskAvailabilityAction({
        providerId,
        patientId,
        localDate: props.date,
        discipline: currentTab,
      })
    );
  }, [providerId, effectivePatient, formik.values.patientId, props.date, currentTab, dispatch]);

  const isTherapyTaskType = (option: { key: string; displayName: string }) => {
    const therapyDisplayNames = ['Therapy', 'Group Therapy', 'Concurrent'];
    const therapyKeys = ['THERAPY', 'GROUP_THERAPY', 'GROUP', 'CONCURRENT', 'CONCUR'];
    return therapyDisplayNames.includes(option.displayName) || therapyKeys.includes(option.key);
  };

  const isTaskTypeDisabled = (option: { key: string; displayName: string }) => {
    // For ALERT_TASK, the alert payload keeps `effectivePatient` populated
    // even after the user clears the Patient Name field. Require the form's
    // own patientId to be set (or the alert prop to be present in non-alert
    // flows) so clearing the field really does disable the Task dropdown.
    const isPatientSelected =
      source === 'ALERT_TASK'
        ? !!formik.values.patientId
        : !!(effectivePatient || formik.values.patientId);
    // Accept either casing for the open-EOC identifier — different sources return
    // `openEocId` (patients list, manual mode) or `openEocid` (therapist-task-list ALERT_TASK).
    const patientHasOpenEoc = !!(
      effectivePatient?.hasOpenEoc ??
      effectivePatient?.openEocId ??
      effectivePatient?.openEocid
    );
    const normalize = (v: string) => (v ?? '').toString().replace(/[\s_]/g, '').toUpperCase();

    if (!isPatientSelected) {
      return true;
    }
    // No active cert for this discipline → Therapy / Group Therapy / Concurrent are off-limits.
    if (lacksCertForTab(effectivePatient) && isTherapyTaskType(option)) {
      return true;
    }
    // Build the set of already-scheduled task types purely from the
    // taskAvailability response so the dropdown always follows the current
    // patient's server-side schedule (including CONCURRENT / GROUP_THERAPY /
    // THERAPY). If we're still on the original ALERT_TASK patient we merge
    // in the alert's scheduleItemType too, since the availability response
    // sometimes hasn't loaded yet on first render.
    const alertUuid = patient?.uuid ?? patient?.patientUuid;
    const isStillAlertPatient =
      source === 'ALERT_TASK' &&
      (!formik?.values?.patientId || formik?.values?.patientId === alertUuid);
    const scheduledSetRaw = [
      ...(scheduledTaskTypes ?? []),
      ...(isStillAlertPatient && patient?.scheduleItemType ? [patient.scheduleItemType] : []),
    ];
    const normalizedScheduled = scheduledSetRaw.map(normalize);
    const isAppointmentOption =
      normalize(option.key) === 'APPOINTMENT' || normalize(option.displayName) === 'APPOINTMENT';
    const isScheduled =
      normalizedScheduled.includes(normalize(option.key)) ||
      normalizedScheduled.includes(normalize(option.displayName));

    // 1) When the appointment-book feature flag is OFF:
    //    - Disable "Appointment"
    //    - Every other option enabled unless it is already scheduled
    if (!isAppointmentBook) {
      if (isAppointmentOption) return true;
      return isScheduled;
    }

    // 2) When the appointment-book feature flag is ON:
    const isGroupOrConcurrent =
      option.key === 'GROUP_THERAPY' ||
      option.key === 'GROUP' ||
      option.key === 'CONCURRENT' ||
      option.key === 'CONCUR' ||
      normalize(option.displayName) === 'GROUPTHERAPY' ||
      normalize(option.displayName) === 'CONCURRENT';
    if (effectivePatient?.isOutPatient) {
      // Outpatient → only Group Therapy / Concurrent are disabled.
      if (isGroupOrConcurrent) return true;
      return isScheduled;
    }
    // Non-outpatient → Appointment always disabled;
    // Therapy / Group / Concurrent + other options enabled unless already scheduled.
    if (isAppointmentOption) return true;
    return isScheduled;
  };

  return (
    <>
      <form
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
        >
          <Typography variant="h6">Add New Task</Typography>
        </DialogTitle>
        <Box sx={{ p: 3, width: 600 }}>
          <Grid
            container
            direction={'column'}
            rowSpacing={'20'}
            xs={12}
          >
            <Grid
              xs={12}
              item
            >
              {
                <Grid
                  xs={12}
                  item
                >
                  <RadioGroup
                    row
                    value={patientSelectionMode}
                    onChange={(e) => {
                      const newMode = e.target.value as 'facility' | 'manual';
                      const previousMode = patientSelectionMode;

                      // Clear any pending intervals when switching modes
                      if (intervalTime) {
                        clearInterval(intervalTime);
                        setIntervalTime(null);
                      }

                      setPatientSelectionMode(newMode);
                      formik.setFieldValue('patientId', '');
                      formik.setFieldValue('taskNote', '');
                      setManualInputValue('');
                      setSearchName('');
                      setSelectedPatientData(null);
                      // Set default task type based on mode
                      if (newMode === 'manual') {
                        formik.setFieldValue('tasksType', 'EVALUATION');
                      } else {
                        formik.setFieldValue('tasksType', 'EVALUATION');
                        if (previousMode === 'manual') {
                          setSearchName('');
                          setPageNo(0);
                          setAllPatients([]);
                        }
                      }
                    }}
                  >
                    <FormControlLabel
                      value="facility"
                      control={<Radio />}
                      label="Select a patient from the facility"
                    />
                    <FormControlLabel
                      value="manual"
                      control={<Radio />}
                      label="Enter a Future Admission manually"
                      disabled={(() => {
                        const propDate = (props as any)?.date;
                        if (!propDate) return false;
                        const parsed = moment(propDate, [
                          'DD-MM-YYYY',
                          'YYYY-MM-DD',
                          moment.ISO_8601,
                        ]);
                        if (!parsed.isValid()) return false;
                        return parsed.startOf('day').isBefore(moment().startOf('day'));
                      })()}
                    />
                  </RadioGroup>
                </Grid>
              }
              {patientSelectionMode === 'manual' ? (
                <Autocomplete
                  key={`manual-autocomplete-${patientSelectionMode}-${
                    formik.values.patientId || 'empty'
                  }`}
                  disablePortal
                  id="combo-box-demo-manual"
                  freeSolo
                  clearOnBlur={false}
                  selectOnFocus={false}
                  filterOptions={(x) => x}
                  options={manualModeOptions}
                  value={getCurrentValue}
                  open={autocompleteOpen}
                  onOpen={() => {
                    setAutocompleteOpen(true);
                    setSearchName('');
                    setPageNo(0);
                    if (intervalTime) clearInterval(intervalTime);
                  }}
                  onClose={(_event) => {
                    setAutocompleteOpen(false);
                  }}
                  getOptionLabel={(option: any) => {
                    if (option?.__isAddNewAdmission) {
                      return '';
                    }
                    if (typeof option === 'string') {
                      return option;
                    }
                    return option ? `${option?.name}` : searchDefaultValue;
                  }}
                  inputValue={manualInputValue}
                  onInputChange={(_event, newInputValue) => {
                    // Only update search if still in manual mode
                    if (patientSelectionMode !== 'manual') {
                      return;
                    }
                    setManualInputValue(newInputValue);
                    setPageNo(0);
                    if (intervalTime) clearInterval(intervalTime);
                    const interval = setInterval(() => {
                      // Double check we're still in manual mode before setting search
                      if (patientSelectionMode === 'manual') {
                        setSearchName(newInputValue);
                      }
                    }, 600);
                    setIntervalTime(interval);
                  }}
                  ListboxComponent={patientSelectionMode === 'manual' ? CustomListbox : undefined}
                  ListboxProps={{
                    role: 'closeMenu',
                    style: {
                      maxHeight: '200px',
                      position: 'relative',
                    },
                    onScroll: handleScrollCapture,
                  }}
                  onChange={(_event, newValue: any) => {
                    if (newValue === null) {
                      formik.setFieldValue('patientId', '');
                      setManualInputValue('');
                      setSelectedPatientData(null);
                      return;
                    }
                    // If newValue is a string (free text), it means user typed something not in list
                    if (typeof newValue === 'string') {
                      formik.setFieldValue('patientId', '');
                      setManualInputValue(newValue);
                      setSelectedPatientData(null);
                    } else {
                      // User selected from list
                      const patientName = `${newValue?.lastName} ${newValue?.firstName}`;
                      // Update state synchronously
                      setSelectedPatientData(newValue);
                      setManualInputValue(patientName);
                      formik.setFieldValue('patientId', newValue?.uuid);
                    }
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!(formik.touched.patientId && formik.errors.patientId)}
                      helperText={formik.touched.patientId && (formik.errors.patientId as any)}
                      label="Patient Name"
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': schedulingTestId('add-task-patient'),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option: any, value: any) => {
                    if (typeof option === 'string' || typeof value === 'string') {
                      return option === value;
                    }
                    return option?.uuid === value?.uuid;
                  }}
                  renderOption={(props, option: any) => {
                    if (typeof option === 'string') {
                      return (
                        <li
                          {...props}
                          key={option}
                        >
                          {option}
                        </li>
                      );
                    }
                    return (
                      <li
                        {...props}
                        key={option?.uuid}
                      >
                        <Box
                          display={'flex'}
                          key={option?.uuid}
                          gap={'5px'}
                        >
                          {patientSelectionMode === 'manual'
                            ? option?.name
                            : `${option?.lastName}, ${option?.firstName}`}
                          <Box
                            sx={{
                              display: !option?.isNonTherapy ? 'none' : 'flex',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '2px 8px',
                              width: 'max-content',
                              alignItems: 'center',
                              borderRadius: '100px',
                              backgroundColor: 'rgba(38, 45, 47, 0.12)',
                              color: 'rgb(90, 112, 119)',
                            }}
                          >
                            Non Therapy
                          </Box>
                        </Box>
                      </li>
                    );
                  }}
                  PaperComponent={patientSelectionMode === 'manual' ? CustomPaper : undefined}
                  componentsProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 4],
                          },
                        },
                      ],
                    },
                  }}
                  popupIcon={
                    gettingPatientList?.loading ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    ) : null
                  }
                  noOptionsText={
                    gettingPatientList?.loading ? 'Loading... Patient List' : 'No Patient Found'
                  }
                />
              ) : (
                <Autocomplete
                  key={`facility-autocomplete-${patientSelectionMode}-${
                    formik.values.patientId || 'empty'
                  }`}
                  disablePortal
                  id="combo-box-demo"
                  clearOnBlur={false}
                  selectOnFocus={false}
                  filterOptions={(x) => x}
                  defaultValue={searchDefaultValue}
                  options={(() => {
                    const alertUuid = patient?.uuid ?? patient?.patientUuid;
                    if (
                      source === 'ALERT_TASK' &&
                      patient &&
                      alertUuid &&
                      !allPatients.some((p: any) => p?.uuid === alertUuid)
                    ) {
                      const normalizedAlertPatient = {
                        ...patient,
                        uuid: alertUuid,
                        firstName:
                          patient?.firstName ??
                          patient?.patientFirstname ??
                          patient?.patientFirstName,
                        lastName:
                          patient?.lastName ?? patient?.patientLastname ?? patient?.patientLastName,
                      };
                      return [normalizedAlertPatient, ...allPatients];
                    }
                    return allPatients;
                  })()}
                  value={
                    formik.values.patientId
                      ? allPatients.find((p: any) => p.uuid === formik.values.patientId) ||
                        (source === 'ALERT_TASK' &&
                        (patient?.uuid === formik.values.patientId ||
                          patient?.patientUuid === formik.values.patientId)
                          ? {
                              ...patient,
                              uuid: patient?.uuid ?? patient?.patientUuid,
                              firstName:
                                patient?.firstName ??
                                patient?.patientFirstname ??
                                patient?.patientFirstName,
                              lastName:
                                patient?.lastName ??
                                patient?.patientLastname ??
                                patient?.patientLastName,
                            }
                          : null)
                      : null
                  }
                  getOptionLabel={(option: any) => {
                    if (!option) return searchDefaultValue;
                    const last =
                      option?.lastName ?? option?.patientLastname ?? option?.patientLastName;
                    const first =
                      option?.firstName ?? option?.patientFirstname ?? option?.patientFirstName;
                    const composed = `${last ?? ''} ${first ?? ''}`.trim();
                    return composed || option?.name || option?.patientName || searchDefaultValue;
                  }}
                  ListboxProps={{
                    role: 'closeMenu',
                    style: {
                      maxHeight: '200px',
                    },
                    onScroll: handleScrollCapture,
                  }}
                  onChange={(_event, newValue: any) => {
                    if (newValue === null) {
                      formik.setFieldValue('patientId', '');
                      return;
                    }
                    formik.setFieldValue('patientId', newValue?.uuid);
                  }}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!(formik.touched.patientId && formik.errors.patientId)}
                      helperText={formik.touched.patientId && (formik.errors.patientId as any)}
                      label="Patient Name"
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': schedulingTestId('add-task-patient'),
                      }}
                      onChange={(event) => {
                        setPageNo(0);
                        if (intervalTime) clearInterval(intervalTime);
                        const interval = setInterval(() => {
                          setSearchName(event.target.value);
                        }, 600);
                        setIntervalTime(interval);
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option: any, value: any) => option?.uuid === value?.uuid}
                  renderOption={(props, option: any) => (
                    <li
                      {...props}
                      key={option?.uuid}
                    >
                      <Box
                        display={'flex'}
                        key={option?.uuid}
                        gap={'5px'}
                      >
                        {`${option?.lastName}, ${option?.firstName}`}
                        <Box
                          sx={{
                            display: !option?.isNonTherapy ? 'none' : 'flex',
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '2px 8px',
                            width: 'max-content',
                            alignItems: 'center',
                            borderRadius: '100px',
                            backgroundColor: 'rgba(38, 45, 47, 0.12)',
                            color: 'rgb(90, 112, 119)',
                          }}
                        >
                          Non Therapy
                        </Box>
                      </Box>
                    </li>
                  )}
                  popupIcon={
                    gettingPatientList?.loading ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                      />
                    ) : null
                  }
                  noOptionsText={
                    gettingPatientList?.loading ? 'Loading... Patient List' : 'No Patient Found'
                  }
                />
              )}
            </Grid>

            <Grid
              xs={12}
              item
            >
              <TextField
                fullWidth
                label="Task"
                error={!!(formik.touched.tasksType && formik.errors.tasksType)}
                helperText={formik.touched.tasksType && formik.errors.tasksType}
                name="tasksType"
                inputProps={{ 'data-testid': schedulingTestId('add-task-type') }}
                onBlur={formik.handleBlur}
                value={formik.values.tasksType}
                onChange={(e) => {
                  const trimmedValue = e.target.value;

                  formik.setFieldValue('tasksType', trimmedValue);
                }}
                select
                SelectProps={{ native: true }}
              >
                {!formik.values.tasksType && (
                  <option
                    key="__empty"
                    value=""
                    disabled
                    hidden
                  />
                )}
                {taskType?.map((option: any) => {
                  // TEMP DEBUG — remove after verifying
                  // eslint-disable-next-line no-console
                  const normalize = (v: string) =>
                    (v ?? '').toString().replace(/[\s_]/g, '').toUpperCase();
                  // Hide APPOINTMENT entirely from the dropdown.
                  if (
                    normalize(option?.key) === 'APPOINTMENT' ||
                    normalize(option?.displayName) === 'APPOINTMENT'
                  ) {
                    return null;
                  }
                  const normalizedScheduled = (scheduledTaskTypes ?? []).map(normalize);
                  const isScheduled =
                    normalizedScheduled.includes(normalize(option?.key)) ||
                    normalizedScheduled.includes(normalize(option?.displayName));
                  // When manual mode is selected, only show Evaluation and Therapy
                  if (patientSelectionMode === 'manual') {
                    if (option.displayName !== 'Evaluation' && option.displayName !== 'Therapy') {
                      return null;
                    }
                    // Still disable any task type already scheduled for this patient,
                    // disable Therapy when the patient is outpatient, and disable it when
                    // the patient has no active cert for this discipline.
                    const isOutpatientTherapy =
                      !!effectivePatient?.isOutPatient && isTherapyTaskType(option);
                    const isNotCertified =
                      lacksCertForTab(effectivePatient) && isTherapyTaskType(option);
                    return (
                      <option
                        key={option.key}
                        value={option.key}
                        disabled={isScheduled || isOutpatientTherapy || isNotCertified}
                      >
                        {option.displayName}
                      </option>
                    );
                  }
                  // Facility mode: show all task types; disable based on patient/EOC selection
                  const isDisabled = isTaskTypeDisabled(option);
                  return (
                    <option
                      key={option.key}
                      value={option.key}
                      disabled={isDisabled}
                    >
                      {option.displayName}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              item
            >
              <TextField
                fullWidth
                label="Number of Minutes"
                name="numberOfMinutes"
                inputProps={{ 'data-testid': schedulingTestId('add-task-minutes') }}
                error={!!(formik.touched.numberOfMinutes && formik.errors.numberOfMinutes)}
                helperText={formik.touched.numberOfMinutes && formik.errors.numberOfMinutes}
                onBlur={formik.handleBlur}
                value={formik.values.numberOfMinutes}
                onChange={(e) => {
                  const trimmedValue = e.target.value;
                  formik.setFieldValue('numberOfMinutes', trimmedValue);
                }}
              />
            </Grid>
          </Grid>
          <Grid
            xs={12}
            sx={{ mt: 3 }}
            item
          >
            <TextField
              fullWidth
              label="Note for Therapist"
              name="taskNote"
              value={formik.values.taskNote}
              inputProps={{ 'data-testid': schedulingTestId('add-task-note') }}
              multiline
              rows={3}
              error={!!(formik.touched.taskNote && formik.errors.taskNote)}
              helperText={formik.touched.taskNote && (formik.errors.taskNote as any)}
              onChange={(e) => {
                const trimmedValue = e.target.value;
                formik.setFieldValue('taskNote', trimmedValue);
              }}
              onBlur={formik.handleBlur}
            />
          </Grid>
          {error && (
            <FormHelperText
              error
              sx={{ mt: 3 }}
            >
              {error as string}
            </FormHelperText>
          )}
        </Box>
        <DialogActions>
          <Button
            autoFocus
            onClick={handleCloseAddTaskForm}
            data-testid={schedulingTestId('add-task-cancel')}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            type="submit"
            disabled={isTaskAdded?.loading}
            data-testid={schedulingTestId('add-task-confirm')}
          >
            Add
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

export default AddTaskForm;
